'use client';

import { useMemo } from 'react';
import type { IUptimeData } from '@/data';
import { Heatmap } from './heatmap';
import { Summary } from './summary';
import { generateSampleUptimeData } from '../utils';
import { startCase } from 'lodash-es';
interface HeatmapCellData {
  date: string;
  hour: number;
  status: 'up' | 'slow' | 'down' | 'no-data';
  avgResponseTime: number;
  count: number;
  incidents: IUptimeData[];
}
export const ServiceHeathCard = ({
  serviceId
}: {
  serviceId: string;
}) => {
  const sampleData: IUptimeData[] = useMemo(() => {
    const data = generateSampleUptimeData(serviceId);
    // Log statistics
    const totalPoints = data.length;
    const upCount = data.filter(d => d.status === 'up').length;
    const slowCount = data.filter(d => d.status === 'slow').length;
    const downCount = data.filter(d => d.status === 'down').length;
    const uptime = (upCount + slowCount * 0.5) / totalPoints * 100;
    const avgResponse = data.filter(d => d.status !== 'down').reduce((sum, d) => sum + d.responseTime, 0) / (upCount + slowCount);
    console.log('Generated Data Statistics:');
    console.log(`- Total Points: ${totalPoints.toLocaleString()}`);
    console.log(`- Uptime: ${uptime.toFixed(3)}%`);
    console.log(`- Average Response: ${avgResponse.toFixed(2)}ms`);
    console.log(`- Down Incidents: ${downCount}`);
    console.log(`- Slow Incidents: ${slowCount}`);
    return data;
  }, [serviceId]);

  // Process data into heatmap grid
  const heatmapData = useMemo(() => {
    // Filter data by serviceId if provided
    const filteredData = serviceId ? sampleData.filter(item => item.serviceId === serviceId) : sampleData;

    // Create 90 days x 12 rows (2-hour intervals) grid
    const today = new Date();
    const grid: HeatmapCellData[][] = [];

    // Initialize grid with empty cells
    for (let day = 0; day < 90; day++) {
      const dayColumn: HeatmapCellData[] = [];
      const currentDate = new Date(today);
      currentDate.setDate(today.getDate() - (89 - day));
      for (let hourInterval = 0; hourInterval < 12; hourInterval++) {
        const startHour = hourInterval * 2;
        dayColumn.push({
          date: currentDate.toISOString().split('T')[0],
          hour: startHour,
          status: 'no-data',
          avgResponseTime: 0,
          count: 0,
          incidents: []
        });
      }
      grid.push(dayColumn);
    }

    // Fill grid with actual data
    for (const item of filteredData) {
      const itemDate = new Date(item.timestamp);
      const daysDiff = Math.floor((today.getTime() - itemDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff >= 0 && daysDiff < 90) {
        const columnIndex = 89 - daysDiff;
        const hourInterval = Math.floor(itemDate.getHours() / 2);
        if (columnIndex >= 0 && columnIndex < 90 && hourInterval >= 0 && hourInterval < 12) {
          const cell = grid[columnIndex][hourInterval];
          cell.incidents.push(item);
          cell.count++;
          cell.avgResponseTime = (cell.avgResponseTime * (cell.count - 1) + item.responseTime) / cell.count;
        }
      }
    }

    // Determine status for each cell based on incidents
    for (const column of grid) {
      for (const cell of column) {
        if (cell.count > 0) {
          const statuses = cell.incidents.map(i => i.status);
          const downCount = statuses.filter(s => s === 'down').length;
          const slowCount = statuses.filter(s => s === 'slow').length;
          const upCount = statuses.filter(s => s === 'up').length;

          // Determine overall status based on majority and severity
          if (downCount > 0) {
            cell.status = 'down';
          } else if (slowCount > upCount * 0.3) {
            // If slow incidents are >30% of up incidents
            cell.status = 'slow';
          } else {
            cell.status = 'up';
          }
        }
      }
    }
    return grid;
  }, [sampleData, serviceId]);
  return <div className='mb-3 rounded-lg border border-foreground/10 bg-foreground/5 p-4'>
      <div className='mb-6'>
        <div className='flex items-center gap-3'>
          <div className='relative flex h-4 w-4 items-center justify-center'>
            <span className='absolute inline-flex size-full animate-ping rounded-full bg-health-operational opacity-75'></span>
            <span className='relative inline-flex size-3 rounded-full bg-health-operational'></span>
          </div>
          <h2 className='font-bold text-2xl text-foreground'>
            {startCase(serviceId)}
          </h2>
        </div>
        <p className=' text-foreground/60'>
          Last 90 days • Each cell represents 2-hour intervals
        </p>
      </div>
      <Heatmap heatmapData={heatmapData} />
      <Summary heatmapData={heatmapData} />
    </div>;
};